/**
 * {@link DataTable} for activities.
 */
import * as React from 'react';
import { Calendar } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DataTableFilters } from '@/components/common/data-table/DataTableFilters';
import { DataTable } from '@/components/common/data-table';
import { Button } from '@/components/forms/Button';
import { ActivityRow } from './ActivityRow';
import { ActivityCard } from './ActivityCard';
import { ActivitiesFiltersContent } from './ActivitiesFilters';
import { DetailsViewContent } from '@/components/common/details/DetailsViewContent';
import { renderActivityDetails } from './renderActivityDetails';
import { DeleteModal } from '@/components/common/modals/DeleteModal';
import { useDataTable } from '@/hooks/useDataTable';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import {
  getActivities,
  deleteActivity,
  exportActivitiesExcel,
} from '@/services/activities.service';
import { cn } from '@/lib/utils';
import {
  DEFAULT_ACTIVITY_FILTERS,
  ALL_ACTIVITY_FILTER_GROUP_IDS,
  FILTER_GROUP_LABELS_ACTIVITIES,
  type ActivityFilters,
  type ActivityFilterGroupId,
} from './activity-configs';
import { hasActivityActiveFilters, toActivityListParams } from './activity-table-utils';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import type { ViewMode } from '@/types/data-table.types';
import type { ElementWidth } from '@/types/interface-elements.types';
import type { Activity } from '@/types/activity.types';

/** Props for {@link ActivitiesDataTable}. */
interface ActivitiesDataTableProps {
  showCreateButton?: boolean;
  onCreateClick?: () => void;
  activities?: Activity[];
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  expandedRowId?: string | null;
  onToggleRow?: (id: string) => void;
  onCloseDetails?: () => void;
  onEditActivity?: (id: string) => void;
  enableFilters?: boolean;
  defaultFilters?: ActivityFilters;
  layoutWidth?: ElementWidth;
  isSection?: boolean;
  pageActions?: { canEditRow?: boolean; canDeleteRow?: boolean; canExportCsv?: boolean };
}

/** {@link DataTable} for activities. */
export const ActivitiesDataTable: React.FC<ActivitiesDataTableProps> = ({
  activities: externalActivities,
  showCreateButton = false,
  onCreateClick,
  viewMode: externalViewMode,
  onViewModeChange: externalOnViewModeChange,
  expandedRowId,
  onToggleRow,
  onCloseDetails,
  onEditActivity,
  enableFilters = true,
  defaultFilters = DEFAULT_ACTIVITY_FILTERS,
  layoutWidth: layoutWidthProp = 'full',
  isSection = false,
  pageActions,
}) => {
  const queryClient = useQueryClient();
  const showExport = pageActions?.canExportCsv === true;

  const dt = useDataTable<ActivityFilters, ActivityFilterGroupId>({
    tableName: 'activities',
    defaultFilters,
    allFilterGroupIds: ALL_ACTIVITY_FILTER_GROUP_IDS,
    layoutWidth: layoutWidthProp,
    isSection,
    externalViewMode,
    onExternalViewModeChange: externalOnViewModeChange,
    externalExpandedRowId: expandedRowId,
    onCloseDetails,
  });

  const { data: apiResponse, isLoading } = useQuery({
    queryKey: ['activities', dt.currentPage, dt.itemsPerPage, dt.filters],
    queryFn: async () =>
      getActivities(toActivityListParams(dt.filters, dt.currentPage, dt.itemsPerPage)),
    enabled: !externalActivities,
  });

  const data = React.useMemo(
    () => externalActivities ?? (apiResponse?.data || []),
    [externalActivities, apiResponse?.data],
  );
  const totalItems = apiResponse?.total || data.length;
  const totalPages = Math.ceil(totalItems / dt.itemsPerPage) || 1;
  const startIndex = (dt.currentPage - 1) * dt.itemsPerPage;
  const endIndex = startIndex + dt.itemsPerPage;

  const [isExporting, setIsExporting] = React.useState(false);
  const isEmptyForExport = React.useMemo(
    () => (externalActivities ? data.length === 0 : !isLoading && totalItems === 0),
    [externalActivities, data.length, isLoading, totalItems],
  );

  /** Export the current catalog (empty-filter warning first). */
  const handleExportExcel = React.useCallback(async () => {
    if (isEmptyForExport) {
      toast.warning(getErrorMessage('exportEmpty'));
      return;
    }
    setIsExporting(true);
    try {
      await exportActivitiesExcel();
      toast.success('Export Excel réussi !');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsExporting(false);
    }
  }, [isEmptyForExport]);

  const {
    data: expandedActivityData,
    isLoading: isLoadingExpandedActivity,
    isError: isExpandedActivityError,
    error: expandedActivityError,
  } = useQuery({
    queryKey: ['activities', expandedRowId],
    queryFn: async () => {
      if (!expandedRowId) return null;
      const { getActivity } = await import('@/services/activities.service');
      return await getActivity(expandedRowId);
    },
    enabled: !!expandedRowId,
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError: isExpandedActivityError,
    error: expandedActivityError,
    catalogKey: 'activityNotFound',
    onClose: onCloseDetails,
    entityId: expandedRowId,
  });

  const hasActiveFilters = React.useMemo(() => hasActivityActiveFilters(dt.filters), [dt.filters]);

  const deleteMutation = useMutation({
    mutationFn: deleteActivity,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['activities'] });
      dt.setDeleteId(null);
      toast.success('Activité supprimée avec succès !');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const activityToDelete = React.useMemo(
    () => data.find((a) => a.id === dt.deleteId),
    [data, dt.deleteId],
  );

  /** Toggle one status chip and reset pagination. */
  const toggleStatusFilter = React.useCallback(
    (status: string) => {
      dt.setFilters((prev) => ({
        ...prev,
        statuses: prev.statuses.includes(status)
          ? prev.statuses.filter((s) => s !== status)
          : [...prev.statuses, status],
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  /** Toggle one activity-type chip and reset pagination. */
  const toggleActivityTypeFilter = React.useCallback(
    (activityTypeId: string) => {
      dt.setFilters((prev) => ({
        ...prev,
        activityTypeIds: prev.activityTypeIds.includes(activityTypeId)
          ? prev.activityTypeIds.filter((id) => id !== activityTypeId)
          : [...prev.activityTypeIds, activityTypeId],
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setSearchQuery = React.useCallback(
    (query: string) => {
      dt.setFilters((prev) => ({ ...prev, searchQuery: query }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const filtersContent = React.useMemo(
    () => (
      <ActivitiesFiltersContent
        filters={dt.filters}
        allowedFilterIds={dt.allowedFilterIds}
        visibleFilterIds={dt.visibleFilterIds}
        onToggleStatus={toggleStatusFilter}
        onToggleActivityType={toggleActivityTypeFilter}
        onSetSearchQuery={setSearchQuery}
      />
    ),
    [
      dt.filters,
      dt.allowedFilterIds,
      dt.visibleFilterIds,
      toggleStatusFilter,
      toggleActivityTypeFilter,
      setSearchQuery,
    ],
  );

  const filterGroups = React.useMemo(
    () => [
      {
        id: 'filters',
        label: 'Filtres',
        type: 'custom' as const,
        customRender: () => filtersContent,
      },
    ],
    [filtersContent],
  );

  const expandedActivity = expandedActivityData ?? null;

  return (
    <DataTable
      data={data}
      totalItems={totalItems}
      isLoading={isLoading}
      viewMode={dt.viewMode}
      onViewModeChange={dt.handleViewModeChange}
      currentPage={dt.currentPage}
      totalPages={totalPages}
      startIndex={startIndex}
      endIndex={endIndex}
      itemLabel="activités"
      onPageChange={dt.setCurrentPage}
      hasActiveFilters={hasActiveFilters}
      containerRef={dt.containerRef}
      expandedColSpan={6}
      toolbarActions={
        <>
          {enableFilters && (
            <Button
              preset="toolbar-filter"
              className={cn(dt.showFilters && 'section-action-button-active')}
              showBadge={hasActiveFilters}
              onClick={() => {
                const next = !dt.showFilters;
                if (!next) dt.setShowConfigure(false);
                dt.setShowFilters(next);
              }}
            />
          )}
          {showExport && (
            <Button
              preset="toolbar-export"
              onClick={handleExportExcel}
              loading={isExporting}
              disabled={isEmptyForExport}
              tooltipText={
                isEmptyForExport && !isExporting
                  ? getErrorMessage('exportEmpty')
                  : 'Exporter la liste des activités en Excel'
              }
            />
          )}
          {showCreateButton && onCreateClick && (
            <Button
              preset="toolbar-create"
              tooltipText="Créer une activité"
              onClick={() => {
                onCloseDetails?.();
                onCreateClick();
              }}
            />
          )}
        </>
      }
      filtersPanel={
        enableFilters ? (
          <DataTableFilters
            show={dt.showFilters}
            hasActiveFilters={hasActiveFilters}
            filterGroups={filterGroups}
            onClearFilters={dt.clearFilters}
            resultCount={totalItems}
            resultLabel="activité(s)"
            onToggleConfigure={
              dt.allowedFilterIds.length > 0 ? () => dt.setShowConfigure((v) => !v) : undefined
            }
            showConfigure={dt.showConfigure}
            configureContent={
              <div className="flex flex-wrap gap-3">
                {ALL_ACTIVITY_FILTER_GROUP_IDS.filter((id) => dt.allowedFilterIds.includes(id)).map(
                  (id) => {
                    const isOn = dt.filterVisibility[id] ?? true;
                    return (
                      <Button
                        key={id}
                        variant="custom"
                        size="sm"
                        onClick={() => dt.handleToggleFilterVisibility(id)}
                        disabled={dt.isSavingFilterVisibility}
                        className={cn(
                          'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                          dt.isSavingFilterVisibility && 'opacity-60 cursor-not-allowed',
                          isOn
                            ? 'bg-[var(--color-2-nuance-1)] border-[var(--color-2-nuance-1)] text-white'
                            : 'bg-transparent border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)] text-muted-foreground hover:border-[var(--color-1-nuance-2)]',
                        )}
                      >
                        {FILTER_GROUP_LABELS_ACTIVITIES[id]}
                      </Button>
                    );
                  },
                )}
              </div>
            }
          />
        ) : undefined
      }
      renderTableHead={() => (
        <tr>
          <th>Code</th>
          <th>Libellé</th>
          <th>Type</th>
          <th>Description</th>
          <th>Statut</th>
          <th className="w-20 text-center">Actions</th>
        </tr>
      )}
      renderTableRow={(activity) => (
        <ActivityRow
          activity={activity}
          onView={onToggleRow ? () => onToggleRow(activity.id) : undefined}
          onEdit={onEditActivity}
          onDelete={(id) => dt.setDeleteId(id)}
          pageActions={pageActions}
        />
      )}
      expandedRowId={dt.expandedRowId}
      renderExpandedRow={(activity) => (
        <DetailsViewContent>{renderActivityDetails(activity)}</DetailsViewContent>
      )}
      expandedRowRef={dt.expandedRowRef}
      expandedCollapseRef={dt.expandedCollapseRef}
      renderCard={(activity) => (
        <ActivityCard
          activity={activity}
          onView={onToggleRow ? () => onToggleRow(activity.id) : undefined}
          onEdit={onEditActivity}
          onDelete={(id) => dt.setDeleteId(id)}
          pageActions={pageActions}
        />
      )}
      emptyIcon={Calendar}
      emptyText="Aucune activité trouvée"
      title="Activités"
      sidebarMenuId="menu-activities"
      isSection={isSection}
      expandedItem={expandedActivity}
      isExpandedDetailLoading={isLoadingExpandedActivity}
      onCloseDetails={onCloseDetails}
      renderDetailsContent={(activity) => (
        <DetailsViewContent>{renderActivityDetails(activity)}</DetailsViewContent>
      )}
      modals={
        <DeleteModal
          open={!!dt.deleteId}
          onClose={() => dt.setDeleteId(null)}
          onConfirm={() => {
            if (dt.deleteId) deleteMutation.mutate(dt.deleteId);
          }}
          itemName={activityToDelete?.label}
          isLoading={deleteMutation.isPending}
        />
      }
    />
  );
};
